/**
 * Row range loading — one `ModelRow.ensureLoaded` call is one load pass.
 *
 * A pass loads each row at most once and never waits on itself. A row is
 * loaded in two stages:
 *
 *   - its **value** — what a row that depends on it needs: a source row's own
 *     cells (its range fetch), a formula's dependencies' values and then its
 *     recompute, a computed row's children, an aggregate's batch;
 *   - its **tree** — what a viewport needs: its value, then every child's
 *     tree.
 *
 * A formula loads the VALUES of the rows it depends on, never their trees.
 * Before this split a formula called its dependency's `ensureLoaded`, which
 * walked the dependency's children; a formula moved under the row it depends
 * on (`reparent`) then loaded parent → formula → parent → … without bound,
 * until V8 ran out of heap.
 *
 * Every stage is started once per pass and awaited by whoever needs it. A
 * wait that would close a loop — a stage waiting, directly or through the
 * stages it waits on, for itself (a true dependency cycle: a computed row
 * whose child formula reads the computed row) — is refused: the waiter
 * carries on with what is loaded, and the pass records the cycle
 * (`RowLoadPass.cycles`, the `row-load/cycle` breadcrumb). A formula cycle
 * proper is caught earlier: the formula engine never evaluates a row in one
 * (`FormulaEngine.cyclicRows`).
 *
 * Each variant declares how it loads from its static block
 * (`declareRowLoader`), the package's replacement for overriding
 * `ensureLoaded`: `ModelRow.ensureLoaded` is the only entry and is not
 * overridden.
 */

import type { Option } from "../core/option.ts";
import type { DateUnit, NaiveDate } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";

/**
 * What a load pass reads of a row: its id (for the cycle breadcrumb) and its
 * children (a tree load's default walk). `ModelRow` is one; a loader names its
 * own row class (`declareRowLoader`).
 */
export interface LoadableRow {
  /** The row's id, as the `row-load/cycle` breadcrumb prints it. */
  readonly id: unknown;
  /** The rows a tree load walks after the value, unless the row's loader says otherwise. */
  readonly children: readonly LoadableRow[];
}

/** What a variant's loader is handed: the pass's range, and its waits. */
export interface RowLoadContext {
  /** The range the pass loads. */
  readonly range: NaiveDate.Range;
  /** The padding the caller asked for (`null`: the fetcher's default). */
  readonly padding: Option<DateUnit>;
  /** Wait for `row`'s value in this pass (started once; a wait that closes a loop resolves at once). */
  value(row: LoadableRow): Promise<void>;
  /** Wait for `row`'s tree in this pass (its value, then every child's tree). */
  tree(row: LoadableRow): Promise<void>;
}

/** How one variant loads. */
export interface RowLoader<T extends LoadableRow> {
  /** Load `row`'s value (see the module doc). */
  readonly value: (row: T, cx: RowLoadContext) => Promise<void>;
  /**
   * The rows whose tree a tree load walks after the value: `row.children` by
   * default. An aggregate whose value already fetched its constituents
   * answers `[]`.
   */
  readonly children: Option<(row: T) => readonly LoadableRow[]>;
}

/** The row classes that declared a loader, most specific first. */
const LOADERS: Array<{
  readonly owns: (row: LoadableRow) => boolean;
  readonly loader: RowLoader<LoadableRow>;
}> = [];

/** A row with no declared loader: no value; its tree is its children's. */
const DEFAULT_LOADER: RowLoader<LoadableRow> = Object.freeze({
  value: () => Promise.resolve(),
  children: null,
});

/**
 * Declare how `cls`'s rows load (`RowLoader`). Called once per class from its
 * static block; a subclass declared later wins over its base. Not on a barrel.
 */
export function declareRowLoader<T extends LoadableRow>(
  cls: abstract new (...args: never[]) => T,
  loader: RowLoader<T>,
): void {
  const children = loader.children;
  LOADERS.unshift({
    owns: (row) => row instanceof cls,
    loader: Object.freeze({
      value: (row: LoadableRow, cx: RowLoadContext) => row instanceof cls ? loader.value(row, cx) : Promise.resolve(),
      children: children == null
        ? null
        : (row: LoadableRow) => row instanceof cls ? children(row) : row.children,
    }),
  });
}

function loaderOf(row: LoadableRow): RowLoader<LoadableRow> {
  for (const entry of LOADERS) if (entry.owns(row)) return entry.loader;
  return DEFAULT_LOADER;
}

type Stage = "value" | "tree";

/** One stage of one row in a pass. */
class StageNode {
  /** Started on first wait; settles when the stage is loaded. */
  promise: Option<Promise<void>> = null;
  /** The stages this one is waiting on right now. */
  readonly waitsOn = new Set<StageNode>();

  constructor(readonly row: LoadableRow, readonly stage: Stage) {}
}

/** A loop the pass refused to wait on: `waiter` needed `needed`, which waits on `waiter`. */
export interface RowLoadCycle {
  readonly waiter: LoadableRow;
  readonly needed: LoadableRow;
}

/** One `ensureLoaded` call's walk (see the module doc). */
export class RowLoadPass {
  readonly #nodes = new Map<LoadableRow, { value: Option<StageNode>; tree: Option<StageNode> }>();
  readonly #cycles: RowLoadCycle[] = [];

  constructor(
    readonly range: NaiveDate.Range,
    readonly padding: Option<DateUnit>,
  ) {}

  /** Load `row`'s tree for the pass's range: the whole of `ModelRow.ensureLoaded`. */
  static load(row: LoadableRow, range: NaiveDate.Range, padding: Option<DateUnit>): Promise<void> {
    return new RowLoadPass(range, padding).#wait(null, row, "tree");
  }

  /** The loops this pass refused to wait on, in the order it met them. */
  get cycles(): readonly RowLoadCycle[] {
    return this.#cycles;
  }

  #node(row: LoadableRow, stage: Stage): StageNode {
    let entry = this.#nodes.get(row);
    if (entry == null) {
      entry = { value: null, tree: null };
      this.#nodes.set(row, entry);
    }
    let node = entry[stage];
    if (node == null) {
      node = new StageNode(row, stage);
      entry[stage] = node;
    }
    return node;
  }

  /** True iff `from` is `to`, or waits (through the stages it waits on) for `to`. */
  #reaches(from: StageNode, to: StageNode): boolean {
    const stack: StageNode[] = [from];
    const seen = new Set<StageNode>();
    for (let node = stack.pop(); node != null; node = stack.pop()) {
      if (node === to) return true;
      if (seen.has(node)) continue;
      seen.add(node);
      for (const next of node.waitsOn) stack.push(next);
    }
    return false;
  }

  #wait(from: Option<StageNode>, row: LoadableRow, stage: Stage): Promise<void> {
    const node = this.#node(row, stage);
    if (from != null) {
      if (this.#reaches(node, from)) {
        this.#cycles.push({ waiter: from.row, needed: row });
        Tracing.log("row-load/cycle", {
          waiter: String(from.row.id),
          waiterStage: from.stage,
          needed: String(row.id),
          neededStage: stage,
        });
        return Promise.resolve();
      }
      from.waitsOn.add(node);
    }
    if (node.promise == null) node.promise = this.#run(node);
    if (from == null) return node.promise;
    const done = () => {
      from.waitsOn.delete(node);
    };
    return node.promise.then(done, (err: unknown) => {
      done();
      throw err;
    });
  }

  #contextFor(node: StageNode): RowLoadContext {
    return {
      range: this.range,
      padding: this.padding,
      value: (row) => this.#wait(node, row, "value"),
      tree: (row) => this.#wait(node, row, "tree"),
    };
  }

  async #run(node: StageNode): Promise<void> {
    // Settle the stage's promise before any waiter reads it: the first wait
    // registers its edge synchronously, so a loop closed by the loader's
    // synchronous part is still seen.
    await Promise.resolve();
    const loader = loaderOf(node.row);
    const cx = this.#contextFor(node);
    if (node.stage === "value") {
      await loader.value(node.row, cx);
      return;
    }
    await cx.value(node.row);
    const children = loader.children == null ? node.row.children : loader.children(node.row);
    await Promise.all(children.map((child) => cx.tree(child)));
  }
}
